// src/pages/Appli/AppliAdmin/VisitorCenter/components/CallbackRequestsPanel.jsx

import React from "react";

import {
  FiMail,
  FiPhone,
  FiPhoneCall,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";

import {
  CALLBACK_STATUS_OPTIONS,
} from "../data/visitorCenterData";

import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  SectionCard,
} from "../ui/VisitorCenterUi";

import {
  formatDateTime,
  formatLocation,
} from "../utils/visitorFormatters";

const STATUS_CLASSES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted:
    "bg-amber-50 text-amber-700 border-amber-200",
  converted:
    "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed:
    "bg-slate-100 text-slate-600 border-slate-200",
  cancelled:
    "bg-red-50 text-red-700 border-red-200",
};

function CallbackStatus({ status }) {
  const label =
    CALLBACK_STATUS_OPTIONS.find(
      (option) => option.value === status,
    )?.label || status;

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${
        STATUS_CLASSES[status] ||
        STATUS_CLASSES.closed
      }`}
    >
      {label}
    </span>
  );
}

export default function CallbackRequestsPanel({
  requests,
  selectedRequest,

  q,
  status,

  page,
  totalPages,

  loading,
  detailsLoading,
  errorMessage,

  onQueryChange,
  onStatusChange,
  onPageChange,
  onSelectRequest,
  onChangeRequestStatus,
  onDeleteRequest,
}) {
  return (
    <div className="grid gap-6 2xl:grid-cols-[minmax(0,1.15fr)_420px]">
      <SectionCard
        title="Demandes de rappel"
        subtitle="Contacts ayant demandé à être rappelés"
      >
        <div className="grid gap-3 border-b border-slate-100 p-4 sm:grid-cols-[1fr_220px]">
          <label className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              value={q}
              onChange={(event) =>
                onQueryChange(event.target.value)
              }
              placeholder="Nom, club, téléphone, email..."
              className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[#2EA7E0]"
            />
          </label>

          <select
            value={status}
            onChange={(event) =>
              onStatusChange(event.target.value)
            }
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#2EA7E0]"
          >
            {CALLBACK_STATUS_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <ErrorState message={errorMessage} />

        {loading ? (
          <LoadingState label="Chargement des demandes..." />
        ) : requests.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#F8FBFD] text-left text-xs uppercase tracking-[0.1em] text-slate-400">
                <tr>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Produit</th>
                  <th className="px-5 py-4">Créée le</th>
                  <th className="px-5 py-4">Statut</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {requests.map((request) => (
                  <tr
                    key={request.id}
                    onClick={() =>
                      onSelectRequest(request.id)
                    }
                    className="cursor-pointer transition hover:bg-[#F8FBFD]"
                  >
                    <td className="px-5 py-4">
                      <div className="font-extrabold text-[#123B57]">
                        {request.name}
                      </div>

                      <div className="mt-1 text-xs text-slate-500">
                        {request.organization_name ||
                          request.phone}
                      </div>
                    </td>

                    <td className="px-5 py-4 text-sm text-slate-600">
                      {request.product_interest || "—"}
                    </td>

                    <td className="px-5 py-4 text-sm text-slate-600">
                      {formatDateTime(
                        request.created_at,
                      )}
                    </td>

                    <td className="px-5 py-4">
                      <CallbackStatus
                        status={request.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={FiPhoneCall}
            title="Aucune demande de rappel"
            description="Les nouvelles demandes commerciales apparaîtront ici."
          />
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          onChange={onPageChange}
        />
      </SectionCard>

      <SectionCard
        title="Détail de la demande"
        subtitle="Coordonnées et historique du visiteur"
      >
        {detailsLoading ? (
          <LoadingState label="Chargement de la demande..." />
        ) : selectedRequest ? (
          <div className="space-y-5 p-5 lg:p-6">
            <div>
              <h3 className="text-xl font-extrabold text-[#123B57]">
                {selectedRequest.name}
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                {selectedRequest.organization_name ||
                  "Organisation non renseignée"}
              </p>

              <div className="mt-3">
                <CallbackStatus
                  status={selectedRequest.status}
                />
              </div>
            </div>

            <div className="space-y-3">
              <a
                href={`tel:${selectedRequest.phone}`}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 transition hover:bg-slate-50"
              >
                <FiPhone className="text-[#2EA7E0]" />

                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
                    Téléphone
                  </div>

                  <div className="mt-1 font-bold text-slate-700">
                    {selectedRequest.phone}
                  </div>
                </div>
              </a>

              {selectedRequest.email && (
                <a
                  href={`mailto:${selectedRequest.email}`}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 transition hover:bg-slate-50"
                >
                  <FiMail className="text-[#2EA7E0]" />

                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
                      Email
                    </div>

                    <div className="mt-1 truncate font-bold text-slate-700">
                      {selectedRequest.email}
                    </div>
                  </div>
                </a>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-[#F8FBFD] p-4">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
                  Produit
                </div>

                <div className="mt-2 font-bold text-slate-700">
                  {selectedRequest.product_interest ||
                    "Non précisé"}
                </div>
              </div>

              <div className="rounded-2xl bg-[#F8FBFD] p-4">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
                  Créneau
                </div>

                <div className="mt-2 font-bold text-slate-700">
                  {selectedRequest.preferred_date ||
                    "Date libre"}
                  {selectedRequest.preferred_time
                    ? ` · ${selectedRequest.preferred_time}`
                    : ""}
                </div>
              </div>
            </div>

            {selectedRequest.message && (
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
                  Message
                </div>

                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600">
                  {selectedRequest.message}
                </p>
              </div>
            )}

            {selectedRequest.session && (
              <div className="rounded-2xl bg-[#EEF7FC] p-4">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#1C567F]">
                  Origine de la demande
                </div>

                <div className="mt-2 text-sm font-semibold text-slate-700">
                  {formatLocation(
                    selectedRequest.session,
                  )}
                </div>

                <div className="mt-1 text-xs text-slate-500">
                  Entrée :{" "}
                  {selectedRequest.session.entry_page ||
                    "inconnue"}
                </div>
              </div>
            )}

            <div className="grid gap-2">
              <select
                value={selectedRequest.status}
                onChange={(event) =>
                  onChangeRequestStatus(
                    selectedRequest.id,
                    event.target.value,
                  )
                }
                className="rounded-xl border border-slate-200 px-3 py-3 text-sm font-semibold outline-none focus:border-[#2EA7E0]"
              >
                {CALLBACK_STATUS_OPTIONS.filter(
                  (option) => option.value,
                ).map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => {
                  const confirmed = window.confirm(
                    "Supprimer définitivement cette demande de rappel ?",
                  );

                  if (confirmed) {
                    onDeleteRequest(
                      selectedRequest.id,
                    );
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 transition hover:bg-red-100"
              >
                <FiTrash2 />
                Supprimer
              </button>
            </div>
          </div>
        ) : (
          <EmptyState
            title="Sélectionnez une demande"
            description="Les informations du contact et son parcours apparaîtront ici."
          />
        )}
      </SectionCard>
    </div>
  );
}